// src/pages/Payment.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Building2,
  Check,
  CheckCircle2,
  CreditCard,
  ExternalLink,
  Gift,
  LoaderCircle,
  LockKeyhole,
  Mail,
  ReceiptText,
  ShieldCheck,
  Sparkles,
  TicketPercent,
  UserRound,
  X,
} from "lucide-react";

import "../sass/pages/payment.scss";

import Logo from "../assets/logoClaro.webp";
import { API_BASE_URL } from "../config/api";
import { useToast } from "../context/ToastContext";

const API_URL = String(API_BASE_URL || "").replace(/\/+$/, "");

const STORAGE_KEYS = {
  REGISTRATION_FLOW: "psyassist:registration-flow",
  SELECTED_PLAN: "psyassist:selected-plan",

  TEMP_SESSION_ID: "psy_temp_register_session_id",
  TEMP_EMAIL: "psy_temp_register_email",
  PLAN_NAME: "psy_temp_register_plan_name",
  PLAN_PRICE: "psy_temp_register_plan_price",
  TIPO_USUARIO: "psy_temp_register_tipo_usuario",
  PRICE_ID: "psy_temp_register_price_id",

  CHECKOUT_PLAN_NAME: "checkoutPlanName",
  CHECKOUT_PLAN_PRICE: "checkoutPlanPrice",
  CHECKOUT_TIPO_USUARIO: "checkoutTipoUsuario",
  CHECKOUT_CRP: "checkoutCrp",
  CHECKOUT_EMAIL: "checkoutEmail",
  CHECKOUT_SESSION_ID: "checkoutSessionIdTemp",
  CHECKOUT_PRICE_ID: "checkoutPriceId",
  CHECKOUT_COUPON_CODE: "checkoutCouponCode",
  CHECKOUT_COUPON_NAME: "checkoutCouponName",
  CHECKOUT_COUPON_DISCOUNT: "checkoutCouponDiscount",
  CHECKOUT_COUPON_MONTHS: "checkoutCouponMonths",
  CHECKOUT_COUPON_APPLIED: "checkoutCouponApplied",
};

const EMPTY_PAYMENT_FLOW = {
  planName: "Plano não identificado",
  planPrice: "Valor não informado",
  tipoUsuario: "individual",
  sessionId: "",
  priceId: "",
  email: "",
  crp: "",
  trialDays: 0,
  cupomAplicado: false,
  cupomCodigo: "",
  cupomNome: "",
  cupomDescontoPercentual: 0,
  cupomDuracaoMeses: 0,
  cupomRestantes: null,
  cupomDescricao: "",
};

const safeParseJson = (value, fallback = null) => {
  if (!value) return fallback;

  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const readStorage = (storage, key) => {
  try {
    return storage.getItem(key) || "";
  } catch {
    return "";
  }
};

const writeStorage = (storage, key, value) => {
  try {
    storage.setItem(key, String(value ?? ""));
    return true;
  } catch {
    return false;
  }
};

const firstValue = (sources, keys, fallback = "") => {
  for (const source of sources) {
    if (!source || typeof source !== "object") continue;

    for (const key of keys) {
      const value = source[key];

      if (value !== undefined && value !== null && String(value).trim()) {
        return value;
      }
    }
  }

  return fallback;
};

const resolvePaymentFlow = (navigationState = {}) => {
  const registrationFlow =
    safeParseJson(
      readStorage(sessionStorage, STORAGE_KEYS.REGISTRATION_FLOW),
      null
    ) || {};

  const selectedPlan =
    safeParseJson(
      readStorage(sessionStorage, STORAGE_KEYS.SELECTED_PLAN),
      null
    ) || {};

  const checkoutFlow = {
    planName: readStorage(localStorage, STORAGE_KEYS.CHECKOUT_PLAN_NAME),
    planPrice: readStorage(localStorage, STORAGE_KEYS.CHECKOUT_PLAN_PRICE),
    tipoUsuario: readStorage(
      localStorage,
      STORAGE_KEYS.CHECKOUT_TIPO_USUARIO
    ),
    crp: readStorage(localStorage, STORAGE_KEYS.CHECKOUT_CRP),
    email: readStorage(localStorage, STORAGE_KEYS.CHECKOUT_EMAIL),
    sessionId: readStorage(
      localStorage,
      STORAGE_KEYS.CHECKOUT_SESSION_ID
    ),
    priceId: readStorage(localStorage, STORAGE_KEYS.CHECKOUT_PRICE_ID),
    cupomCodigo: readStorage(
      localStorage,
      STORAGE_KEYS.CHECKOUT_COUPON_CODE
    ),
    cupomNome: readStorage(
      localStorage,
      STORAGE_KEYS.CHECKOUT_COUPON_NAME
    ),
    cupomDescontoPercentual: readStorage(
      localStorage,
      STORAGE_KEYS.CHECKOUT_COUPON_DISCOUNT
    ),
    cupomDuracaoMeses: readStorage(
      localStorage,
      STORAGE_KEYS.CHECKOUT_COUPON_MONTHS
    ),
    cupomAplicado: readStorage(
      localStorage,
      STORAGE_KEYS.CHECKOUT_COUPON_APPLIED
    ),
  };

  const legacyFlow = {
    planName: readStorage(localStorage, STORAGE_KEYS.PLAN_NAME),
    planPrice: readStorage(localStorage, STORAGE_KEYS.PLAN_PRICE),
    tipoUsuario: readStorage(localStorage, STORAGE_KEYS.TIPO_USUARIO),
    email: readStorage(localStorage, STORAGE_KEYS.TEMP_EMAIL),
    sessionId: readStorage(localStorage, STORAGE_KEYS.TEMP_SESSION_ID),
    priceId: readStorage(localStorage, STORAGE_KEYS.PRICE_ID),
  };

  const sources = [
    navigationState,
    registrationFlow,
    selectedPlan,
    checkoutFlow,
    legacyFlow,
  ];

  const tipoUsuario =
    String(
      firstValue(
        sources,
        ["tipoUsuario"],
        EMPTY_PAYMENT_FLOW.tipoUsuario
      )
    ).toLowerCase() === "clinica"
      ? "clinica"
      : "individual";

  const individualPrice = firstValue(
    sources,
    [
      "planPrice",
      "planPriceIndividual",
      "valorPlano",
      "pricePayload",
    ],
    EMPTY_PAYMENT_FLOW.planPrice
  );

  const clinicPrice = firstValue(
    sources,
    [
      "planPrice",
      "planPriceClinica",
      "planPriceIndividual",
      "valorPlano",
      "pricePayload",
    ],
    individualPrice
  );

  return {
    planName: String(
      firstValue(
        sources,
        ["planName", "plano", "name"],
        EMPTY_PAYMENT_FLOW.planName
      )
    ).trim(),

    planPrice: String(
      tipoUsuario === "clinica" ? clinicPrice : individualPrice
    ).trim(),

    tipoUsuario,

    sessionId: String(
      firstValue(
        sources,
        ["sessionId", "sessionIdTemp"],
        EMPTY_PAYMENT_FLOW.sessionId
      )
    ).trim(),

    priceId: String(
      firstValue(
        sources,
        ["priceId"],
        EMPTY_PAYMENT_FLOW.priceId
      )
    ).trim(),

    email: String(
      firstValue(
        sources,
        ["email"],
        EMPTY_PAYMENT_FLOW.email
      )
    )
      .trim()
      .toLowerCase(),

    crp: String(
      firstValue(
        sources,
        ["crp"],
        EMPTY_PAYMENT_FLOW.crp
      )
    ).trim(),

    trialDays: Math.max(
      Number(
        firstValue(
          sources,
          ["trialDays"],
          EMPTY_PAYMENT_FLOW.trialDays
        )
      ) || 0,
      0
    ),

    cupomAplicado:
      String(
        firstValue(
          sources,
          ["cupomAplicado"],
          EMPTY_PAYMENT_FLOW.cupomAplicado
        )
      ).toLowerCase() === "true",

    cupomCodigo: normalizeCouponCode(
      firstValue(
        sources,
        ["cupomCodigo"],
        EMPTY_PAYMENT_FLOW.cupomCodigo
      )
    ),

    cupomNome: String(
      firstValue(
        sources,
        ["cupomNome"],
        EMPTY_PAYMENT_FLOW.cupomNome
      )
    ).trim(),

    cupomDescontoPercentual: Math.max(
      Number(
        firstValue(
          sources,
          ["cupomDescontoPercentual"],
          EMPTY_PAYMENT_FLOW.cupomDescontoPercentual
        )
      ) || 0,
      0
    ),

    cupomDuracaoMeses: Math.max(
      Number(
        firstValue(
          sources,
          ["cupomDuracaoMeses"],
          EMPTY_PAYMENT_FLOW.cupomDuracaoMeses
        )
      ) || 0,
      0
    ),

    cupomRestantes: firstValue(
      sources,
      ["cupomRestantes"],
      EMPTY_PAYMENT_FLOW.cupomRestantes
    ),

    cupomDescricao: String(
      firstValue(
        sources,
        ["cupomDescricao"],
        EMPTY_PAYMENT_FLOW.cupomDescricao
      )
    ).trim(),
  };
};

const getApiMessage = async (
  response,
  fallback = "Não foi possível concluir a operação."
) => {
  const payload = await response.json().catch(() => null);

  return (
    payload?.message ||
    payload?.error ||
    payload?.detail ||
    fallback
  );
};

const wait = (milliseconds) =>
  new Promise((resolve) => window.setTimeout(resolve, milliseconds));


const normalizeCouponCode = (value = "") =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9_-]/g, "")
    .slice(0, 40);

const parseMoneyValue = (value) => {
  const raw = String(value || "").trim();

  if (!raw) return null;

  const match = raw.match(
    /(\d{1,3}(?:\.\d{3})*,\d{2}|\d+(?:[.,]\d{1,2})?)/
  );

  if (!match) return null;

  let normalized = match[1];

  if (normalized.includes(",")) {
    normalized = normalized.replace(/\./g, "").replace(",", ".");
  } else {
    normalized = normalized.replace(",", ".");
  }

  const number = Number(normalized);

  return Number.isFinite(number) ? number : null;
};

const formatMoneyBR = (value) => {
  if (!Number.isFinite(value)) return "";

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

const Payment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  const [flow, setFlow] = useState(() =>
    resolvePaymentFlow(location.state || {})
  );

  const [stage, setStage] = useState("idle");
  const [couponCode, setCouponCode] = useState(
    () => resolvePaymentFlow(location.state || {}).cupomCodigo || ""
  );
  const [couponStage, setCouponStage] = useState("idle");

  useEffect(() => {
    const resolved = resolvePaymentFlow(location.state || {});
    setFlow(resolved);

    if (resolved.cupomAplicado && resolved.cupomCodigo) {
      setCouponCode(resolved.cupomCodigo);
    }
  }, [location.state]);

  const isCheckoutLoading = stage !== "idle";
  const isCouponLoading = couponStage !== "idle";
  const isLoading = isCheckoutLoading || isCouponLoading;

  const accountTypeLabel =
    flow.tipoUsuario === "clinica"
      ? "Conta clínica"
      : "Conta profissional";

  const normalPlanValue = useMemo(
    () => parseMoneyValue(flow.planPrice),
    [flow.planPrice]
  );

  const promotionalPlanValue = useMemo(() => {
    if (
      !flow.cupomAplicado ||
      !flow.cupomDescontoPercentual ||
      !Number.isFinite(normalPlanValue)
    ) {
      return null;
    }

    return (
      normalPlanValue *
      (1 - flow.cupomDescontoPercentual / 100)
    );
  }, [
    flow.cupomAplicado,
    flow.cupomDescontoPercentual,
    normalPlanValue,
  ]);

  const promotionalPlanPrice = useMemo(
    () =>
      Number.isFinite(promotionalPlanValue)
        ? `${formatMoneyBR(promotionalPlanValue)}/mês`
        : "",
    [promotionalPlanValue]
  );

  const discountAmountValue = useMemo(() => {
    if (
      !flow.cupomAplicado ||
      !Number.isFinite(normalPlanValue) ||
      !Number.isFinite(promotionalPlanValue)
    ) {
      return null;
    }

    return Math.max(
      normalPlanValue - promotionalPlanValue,
      0
    );
  }, [
    flow.cupomAplicado,
    normalPlanValue,
    promotionalPlanValue,
  ]);

  const discountAmountPrice = useMemo(
    () =>
      Number.isFinite(discountAmountValue)
        ? formatMoneyBR(discountAmountValue)
        : "",
    [discountAmountValue]
  );

  const couponPeriodLabel = useMemo(() => {
    if (!flow.cupomAplicado) return "";

    if (flow.cupomDuracaoMeses > 0) {
      return `${flow.cupomDuracaoMeses} ${
        flow.cupomDuracaoMeses === 1 ? "mês" : "meses"
      }`;
    }

    return "período promocional";
  }, [
    flow.cupomAplicado,
    flow.cupomDuracaoMeses,
  ]);

  const trialLabel = useMemo(() => {
    if (flow.trialDays <= 0) return "";

    return `${flow.trialDays} ${
      flow.trialDays === 1 ? "dia" : "dias"
    } grátis`;
  }, [flow.trialDays]);

  const persistPaymentFlow = (nextFlow) => {
    setFlow(nextFlow);

    try {
      const current =
        safeParseJson(
          readStorage(
            sessionStorage,
            STORAGE_KEYS.REGISTRATION_FLOW
          ),
          {}
        ) || {};

      sessionStorage.setItem(
        STORAGE_KEYS.REGISTRATION_FLOW,
        JSON.stringify({
          ...current,
          ...nextFlow,
        })
      );
    } catch {
      // O fluxo continua salvo nas chaves de checkout.
    }
  };

  const validationIssues = useMemo(() => {
    const issues = [];

    if (!flow.email) {
      issues.push(
        "O e-mail da assinatura não foi localizado."
      );
    }

    if (!flow.priceId) {
      issues.push(
        "O identificador do plano de pagamento está ausente."
      );
    }

    if (!flow.sessionId) {
      issues.push(
        "A sessão temporária do cadastro não foi localizada."
      );
    }

    return issues;
  }, [flow.email, flow.priceId, flow.sessionId]);

  const stageContent = useMemo(() => {
    const map = {
      preparing: {
        title: "Preparando seus dados",
        message: "Organizando as informações da assinatura.",
        progress: 20,
      },
      creating: {
        title: "Criando checkout seguro",
        message: "Solicitando uma sessão protegida à Stripe.",
        progress: 48,
      },
      linking: {
        title: "Vinculando sua assinatura",
        message: "Associando o checkout ao seu cadastro.",
        progress: 74,
      },
      redirecting: {
        title: "Checkout pronto",
        message: "Redirecionando para o ambiente seguro de pagamento.",
        progress: 100,
      },
    };

    return map[stage] || null;
  }, [stage]);

  const saveCheckoutFlow = () => {
    const values = [
      [STORAGE_KEYS.CHECKOUT_PLAN_NAME, flow.planName],
      [STORAGE_KEYS.CHECKOUT_PLAN_PRICE, flow.planPrice],
      [STORAGE_KEYS.CHECKOUT_TIPO_USUARIO, flow.tipoUsuario],
      [STORAGE_KEYS.CHECKOUT_CRP, flow.crp],
      [STORAGE_KEYS.CHECKOUT_EMAIL, flow.email],
      [STORAGE_KEYS.CHECKOUT_SESSION_ID, flow.sessionId],
      [STORAGE_KEYS.CHECKOUT_PRICE_ID, flow.priceId],
      [STORAGE_KEYS.CHECKOUT_COUPON_CODE, flow.cupomCodigo || ""],
      [STORAGE_KEYS.CHECKOUT_COUPON_NAME, flow.cupomNome || ""],
      [
        STORAGE_KEYS.CHECKOUT_COUPON_DISCOUNT,
        flow.cupomDescontoPercentual || ""
      ],
      [
        STORAGE_KEYS.CHECKOUT_COUPON_MONTHS,
        flow.cupomDuracaoMeses || ""
      ],
      [
        STORAGE_KEYS.CHECKOUT_COUPON_APPLIED,
        flow.cupomAplicado ? "true" : "false"
      ],
    ];

    values.forEach(([key, value]) => {
      writeStorage(localStorage, key, value);
    });

    try {
      sessionStorage.setItem(
        STORAGE_KEYS.REGISTRATION_FLOW,
        JSON.stringify({
          ...flow,
          paymentStartedAt: new Date().toISOString(),
        })
      );
    } catch {
      // O fluxo também permanece salvo nas chaves legadas.
    }
  };

  const updateTemporaryRegistrationWithStripe = async (
    stripeSessionId
  ) => {
    if (!stripeSessionId || !flow.sessionId) return;

    const response = await fetch(
      `${API_URL}/psy/temp-register/${encodeURIComponent(
        flow.sessionId
      )}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          stripeSessionId,
        }),
      }
    );

    if (!response.ok) {
      throw new Error(
        await getApiMessage(
          response,
          "Não foi possível vincular o checkout ao cadastro temporário."
        )
      );
    }
  };


  const applyCoupon = async () => {
    if (isCouponLoading || isCheckoutLoading) return;

    const codigo = normalizeCouponCode(couponCode);

    if (!codigo) {
      toast.warning({
        title: "Informe o cupom",
        message: "Digite o código promocional antes de aplicar.",
      });
      return;
    }

    if (!flow.sessionId) {
      toast.error({
        title: "Sessão de cadastro inválida",
        message:
          "Não foi possível identificar o cadastro para aplicar o cupom.",
        duration: 0,
      });
      return;
    }

    setCouponStage("applying");

    try {
      const response = await fetch(
        `${API_URL}/psy/cupom/${encodeURIComponent(
          flow.sessionId
        )}/aplicar`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            codigo,
          }),
        }
      );

      const payload = await response.json().catch(() => null);

      if (!response.ok || payload?.success === false) {
        throw new Error(
          payload?.message ||
            payload?.error ||
            "Cupom inválido ou indisponível."
        );
      }

      const nextFlow = {
        ...flow,
        cupomAplicado: true,
        cupomCodigo: normalizeCouponCode(payload?.codigo || codigo),
        cupomNome: String(payload?.nome || "").trim(),
        cupomDescontoPercentual: Math.max(
          Number(payload?.descontoPercentual) || 0,
          0
        ),
        cupomDuracaoMeses: Math.max(
          Number(payload?.duracaoMeses) || 0,
          0
        ),
        cupomRestantes:
          payload?.restantes === undefined ||
          payload?.restantes === null
            ? null
            : Number(payload.restantes),
        cupomDescricao: String(
          payload?.descricaoPublica || ""
        ).trim(),
      };

      setCouponCode(nextFlow.cupomCodigo);
      persistPaymentFlow(nextFlow);

      toast.success({
        title:
          nextFlow.cupomCodigo === "FUNDADOR"
            ? "Membro Fundador desbloqueado"
            : "Cupom aplicado",
        message:
          `${nextFlow.cupomDescontoPercentual}% de desconto` +
          `${
            nextFlow.cupomDuracaoMeses
              ? ` por ${nextFlow.cupomDuracaoMeses} meses`
              : ""
          }.`,
      });
    } catch (error) {
      toast.error({
        title: "Não foi possível aplicar o cupom",
        message:
          error?.message ||
          "Revise o código informado e tente novamente.",
      });
    } finally {
      setCouponStage("idle");
    }
  };

  const removeCoupon = async () => {
    if (
      isCouponLoading ||
      isCheckoutLoading ||
      !flow.sessionId ||
      !flow.cupomAplicado
    ) {
      return;
    }

    setCouponStage("removing");

    try {
      const response = await fetch(
        `${API_URL}/psy/cupom/${encodeURIComponent(
          flow.sessionId
        )}`,
        {
          method: "DELETE",
          headers: {
            Accept: "application/json",
          },
        }
      );

      const payload = await response.json().catch(() => null);

      if (!response.ok || payload?.success === false) {
        throw new Error(
          payload?.message ||
            payload?.error ||
            "Não foi possível remover o cupom."
        );
      }

      const nextFlow = {
        ...flow,
        cupomAplicado: false,
        cupomCodigo: "",
        cupomNome: "",
        cupomDescontoPercentual: 0,
        cupomDuracaoMeses: 0,
        cupomRestantes: null,
        cupomDescricao: "",
      };

      setCouponCode("");
      persistPaymentFlow(nextFlow);

      toast.info({
        title: "Cupom removido",
        message:
          "A contratação voltou às condições normais do plano.",
      });
    } catch (error) {
      toast.error({
        title: "Não foi possível remover o cupom",
        message:
          error?.message ||
          "Tente novamente antes de iniciar o checkout.",
      });
    } finally {
      setCouponStage("idle");
    }
  };

  const handleCouponKeyDown = (event) => {
    if (event.key !== "Enter") return;

    event.preventDefault();
    applyCoupon();
  };

  const createStripeSession = async () => {
    const response = await fetch(`${API_URL}/stripe`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: flow.email,
        priceId: flow.priceId,
        sessionIdTemp: flow.sessionId,
        frontendUrl: window.location.origin,
      }),
    });

    const payload = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(
        payload?.message ||
          payload?.error ||
          "Não foi possível criar a sessão de pagamento."
      );
    }

    if (!payload?.url) {
      throw new Error(
        "O serviço de pagamento não retornou a URL do checkout."
      );
    }

    return payload;
  };

  const handleGoToCheckout = async () => {
    if (isLoading) return;

    if (validationIssues.length > 0) {
      toast.warning({
        title: "Fluxo de assinatura incompleto",
        message:
          "Retorne à etapa anterior para recuperar os dados necessários.",
        duration: 0,
      });

      return;
    }

    setStage("preparing");

    try {
      saveCheckoutFlow();

      await wait(180);

      setStage("creating");
      const stripeSession = await createStripeSession();

      if (stripeSession?.id) {
        setStage("linking");

        await updateTemporaryRegistrationWithStripe(
          stripeSession.id
        );
      }

      setStage("redirecting");
      await wait(420);

      navigate(
        `/payment-checkout-webview?url=${encodeURIComponent(
          stripeSession.url
        )}`,
        {
          replace: false,
          state: {
            checkoutUrl: stripeSession.url,
            stripeSessionId: stripeSession.id || "",
            sessionId: flow.sessionId,
          },
        }
      );
    } catch (error) {
      console.error("Erro ao iniciar o pagamento:", error);

      setStage("idle");

      toast.error({
        title: "Não foi possível abrir o checkout",
        message:
          error?.message ||
          "O serviço de pagamento não respondeu como esperado.",
        actionLabel: "Tentar novamente",
        onAction: handleGoToCheckout,
        duration: 0,
      });
    }
  };

  const handleBack = () => {
    if (isLoading) return;
    navigate(-1);
  };

  return (
    <main className="psy-payment-page">
      <div className="psy-payment-background" aria-hidden="true">
        <span className="psy-payment-background__orb psy-payment-background__orb--one" />
        <span className="psy-payment-background__orb psy-payment-background__orb--two" />
        <span className="psy-payment-background__grid" />
      </div>

      <div className="psy-payment-layout">
        <aside className="psy-payment-aside">
          <div className="psy-payment-brand">
            <img src={Logo} alt="PsyAssist Pro" />

            <div>
              <strong>PsyAssist Pro</strong>
              <span>Conclusão segura da assinatura</span>
            </div>
          </div>

          <div className="psy-payment-aside__content">
            <span className="psy-payment-kicker">
              <Sparkles size={15} />
              Última etapa
            </span>

            <h1>Seu espaço profissional está pronto para começar.</h1>

            <p>
              Revise sua contratação e siga para o checkout da Stripe.
              A ativação da conta será processada após a confirmação do
              pagamento.
            </p>

            <div className="psy-payment-assurances">
              <div className="psy-payment-assurance">
                <span>
                  <ShieldCheck size={19} />
                </span>

                <div>
                  <strong>Checkout protegido</strong>
                  <small>
                    O pagamento é processado em ambiente seguro da Stripe.
                  </small>
                </div>
              </div>

              <div className="psy-payment-assurance">
                <span>
                  <BadgeCheck size={19} />
                </span>

                <div>
                  <strong>Ativação automática</strong>
                  <small>
                    A assinatura é vinculada ao cadastro após a confirmação.
                  </small>
                </div>
              </div>
            </div>
          </div>

          <div className="psy-payment-plan-card">
            <div className="psy-payment-plan-card__top">
              <span>{accountTypeLabel}</span>
              <CheckCircle2 size={18} />
            </div>

            <strong>{flow.planName}</strong>

            {flow.cupomAplicado && promotionalPlanPrice ? (
              <div className="psy-payment-plan-card__promo-price">
                <span>{flow.planPrice}</span>
                <p>{promotionalPlanPrice}</p>
              </div>
            ) : (
              <p>{flow.planPrice}</p>
            )}

            <small>
              As condições definitivas da cobrança serão apresentadas antes
              da confirmação no checkout.
            </small>
          </div>
        </aside>

        <section className="psy-payment-panel">
          <div className="psy-payment-progress">
            <div className="psy-payment-progress__header">
              <span>Etapa 3 de 3</span>
              <strong>Pagamento</strong>
            </div>

            <div
              className="psy-payment-progress__track"
              role="progressbar"
              aria-valuemin="0"
              aria-valuemax="100"
              aria-valuenow="100"
              aria-label="Progresso do cadastro"
            >
              <span style={{ width: "100%" }} />
            </div>
          </div>

          <header className="psy-payment-header">
            <div className="psy-payment-header__icon" aria-hidden="true">
              <ReceiptText size={22} />
            </div>

            <div>
              <span>Resumo da contratação</span>
              <h2>Revise antes de continuar</h2>
              <p>
                Nenhum dado de cartão é solicitado nesta tela. Ao continuar,
                você será encaminhado ao checkout seguro.
              </p>
            </div>
          </header>

          <div className="psy-payment-checkout-card">
            <div className="psy-payment-checkout-card__visual">
              <div className="psy-payment-card-art">
                <div className="psy-payment-card-art__top">
                  <span>
                    <CreditCard size={20} />
                  </span>

                  <LockKeyhole size={18} />
                </div>

                <div className="psy-payment-card-art__line">
                  •••• &nbsp; •••• &nbsp; •••• &nbsp; ••••
                </div>

                <div className="psy-payment-card-art__bottom">
                  <small>CHECKOUT SEGURO</small>
                  <strong>STRIPE</strong>
                </div>
              </div>

              <span className="psy-payment-security-badge">
                <ShieldCheck size={15} />
                Ambiente protegido
              </span>
            </div>

            <div className="psy-payment-summary">
              <div className="psy-payment-summary__head">
                <div>
                  <span>Assinatura selecionada</span>
                  <strong>{flow.planName}</strong>
                </div>

                <CheckCircle2 size={20} />
              </div>

              <dl className="psy-payment-summary__list">
                <div>
                  <dt>
                    {flow.tipoUsuario === "clinica" ? (
                      <Building2 size={16} />
                    ) : (
                      <UserRound size={16} />
                    )}
                    Tipo de conta
                  </dt>
                  <dd>{accountTypeLabel}</dd>
                </div>

                <div>
                  <dt>
                    <Mail size={16} />
                    E-mail
                  </dt>
                  <dd>{flow.email || "Não identificado"}</dd>
                </div>

                {flow.crp ? (
                  <div>
                    <dt>
                      <BadgeCheck size={16} />
                      CRP
                    </dt>
                    <dd>{flow.crp}</dd>
                  </div>
                ) : null}
              </dl>


              <section
                className={`psy-payment-coupon ${
                  flow.cupomAplicado
                    ? "psy-payment-coupon--applied"
                    : ""
                }`}
                aria-label="Cupom promocional"
              >
                {!flow.cupomAplicado ? (
                  <>
                    <div className="psy-payment-coupon__head">
                      <span className="psy-payment-coupon__icon">
                        <TicketPercent size={17} />
                      </span>

                      <div>
                        <strong>Possui um cupom?</strong>
                        <small>
                          Insira o código antes de continuar para o checkout.
                        </small>
                      </div>
                    </div>

                    <div className="psy-payment-coupon__form">
                      <div className="psy-payment-coupon__input">
                        <Gift size={16} aria-hidden="true" />

                        <input
                          type="text"
                          value={couponCode}
                          onChange={(event) =>
                            setCouponCode(
                              normalizeCouponCode(event.target.value)
                            )
                          }
                          onKeyDown={handleCouponKeyDown}
                          placeholder="Digite seu código"
                          autoComplete="off"
                          spellCheck="false"
                          disabled={isLoading}
                          aria-label="Código do cupom"
                        />
                      </div>

                      <button
                        type="button"
                        className="psy-payment-coupon__apply"
                        onClick={applyCoupon}
                        disabled={isLoading || !couponCode.trim()}
                      >
                        {couponStage === "applying" ? (
                          <LoaderCircle
                            size={16}
                            className="psy-payment-spin"
                          />
                        ) : (
                          <TicketPercent size={16} />
                        )}
                        Aplicar
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="psy-payment-coupon__success">
                    <div className="psy-payment-coupon__founder-mark">
                      <Sparkles size={18} />
                    </div>

                    <div className="psy-payment-coupon__success-content">
                      <span>
                        {flow.cupomCodigo === "FUNDADOR"
                          ? "Membro Fundador desbloqueado"
                          : "Cupom aplicado"}
                      </span>

                      <strong>{flow.cupomCodigo}</strong>

                      <small>
                        {flow.cupomDescontoPercentual}% OFF
                        {flow.cupomDuracaoMeses
                          ? ` por ${flow.cupomDuracaoMeses} meses`
                          : ""}
                        {flow.cupomCodigo === "FUNDADOR"
                          ? " • selo permanente de fundador"
                          : ""}
                      </small>
                    </div>

                    <button
                      type="button"
                      className="psy-payment-coupon__remove"
                      onClick={removeCoupon}
                      disabled={isLoading}
                      aria-label="Remover cupom"
                    >
                      {couponStage === "removing" ? (
                        <LoaderCircle
                          size={16}
                          className="psy-payment-spin"
                        />
                      ) : (
                        <X size={16} />
                      )}
                      <span>Remover</span>
                    </button>
                  </div>
                )}
              </section>

              {flow.cupomAplicado && promotionalPlanPrice ? (
                <section
                  className="psy-payment-promo-breakdown"
                  aria-label="Resumo do desconto aplicado"
                >
                  <div className="psy-payment-promo-breakdown__head">
                    <span>
                      <Gift size={16} />
                    </span>

                    <div>
                      <strong>Benefício aplicado à assinatura</strong>
                      <small>
                        O desconto abaixo será levado ao checkout seguro da Stripe.
                      </small>
                    </div>
                  </div>

                  <dl className="psy-payment-promo-breakdown__list">
                    <div>
                      <dt>Preço normal</dt>
                      <dd>{flow.planPrice}</dd>
                    </div>

                    <div>
                      <dt>Desconto</dt>
                      <dd className="psy-payment-promo-breakdown__discount">
                        -{flow.cupomDescontoPercentual}%
                        {discountAmountPrice
                          ? ` (${discountAmountPrice}/mês)`
                          : ""}
                      </dd>
                    </div>

                    <div>
                      <dt>
                        Durante {couponPeriodLabel}
                      </dt>
                      <dd className="psy-payment-promo-breakdown__promotional">
                        {promotionalPlanPrice}
                      </dd>
                    </div>

                    {flow.cupomDuracaoMeses > 0 ? (
                      <div>
                        <dt>
                          Depois de {couponPeriodLabel}
                        </dt>
                        <dd>{flow.planPrice}</dd>
                      </div>
                    ) : null}
                  </dl>

                  {flow.trialDays > 0 ? (
                    <div className="psy-payment-promo-breakdown__trial">
                      <CheckCircle2 size={16} />

                      <span>
                        <strong>{trialLabel}.</strong>{" "}
                        Durante o período de teste você não será cobrado.
                        A primeira cobrança seguirá o valor promocional acima.
                      </span>
                    </div>
                  ) : null}
                </section>
              ) : null}

              <div
                className={`psy-payment-summary__total ${
                  flow.cupomAplicado
                    ? "psy-payment-summary__total--promo"
                    : ""
                }`}
              >
                <span>
                  {flow.cupomAplicado
                    ? flow.trialDays > 0
                      ? "Primeira cobrança após o teste"
                      : "Valor promocional"
                    : "Valor apresentado"}
                </span>

                <div className="psy-payment-summary__price-stack">
                  {flow.cupomAplicado ? (
                    <small>{flow.planPrice}</small>
                  ) : null}

                  <strong>
                    {flow.cupomAplicado && promotionalPlanPrice
                      ? promotionalPlanPrice
                      : flow.planPrice}
                  </strong>
                </div>
              </div>
            </div>
          </div>

          {validationIssues.length > 0 ? (
            <section
              className="psy-payment-flow-alert"
              role="alert"
              aria-live="polite"
            >
              <div className="psy-payment-flow-alert__icon">
                <LockKeyhole size={19} />
              </div>

              <div>
                <strong>Dados da assinatura incompletos</strong>
                <p>
                  Não é possível iniciar o checkout enquanto os seguintes
                  dados não forem recuperados:
                </p>

                <ul>
                  {validationIssues.map((issue) => (
                    <li key={issue}>{issue}</li>
                  ))}
                </ul>
              </div>

              <button
                type="button"
                onClick={handleBack}
                disabled={isLoading}
              >
                Voltar e corrigir
              </button>
            </section>
          ) : null}

          {stageContent ? (
            <section
              className="psy-payment-processing"
              role="status"
              aria-live="polite"
            >
              <div className="psy-payment-processing__spinner" />

              <div className="psy-payment-processing__content">
                <strong>{stageContent.title}</strong>
                <span>{stageContent.message}</span>

                <div className="psy-payment-processing__track">
                  <span
                    style={{
                      width: `${stageContent.progress}%`,
                    }}
                  />
                </div>
              </div>
            </section>
          ) : null}

          <div className="psy-payment-trust">
            <div>
              <Check size={16} />
              <span>Pagamento processado pela Stripe</span>
            </div>

            <div>
              <Check size={16} />
              <span>Ativação vinculada ao cadastro</span>
            </div>

            <div>
              <Check size={16} />
              <span>Confirmação antes da cobrança</span>
            </div>
          </div>

          <div className="psy-payment-actions">
            <button
              type="button"
              className="psy-payment-secondary-button"
              onClick={handleBack}
              disabled={isLoading}
            >
              <ArrowLeft size={18} />
              Voltar
            </button>

            <button
              type="button"
              className="psy-payment-primary-button"
              onClick={handleGoToCheckout}
              disabled={isLoading || validationIssues.length > 0}
            >
              <span>
                {isLoading
                  ? "Preparando checkout..."
                  : "Continuar para pagamento"}
              </span>

              {!isLoading ? (
                <ExternalLink size={17} />
              ) : null}
            </button>
          </div>

          <footer className="psy-payment-footer">
            <LockKeyhole size={14} />
            <span>
              Você poderá revisar todos os dados antes de confirmar.
            </span>
            <ArrowRight size={14} aria-hidden="true" />
          </footer>
        </section>
      </div>
    </main>
  );
};

export default Payment;
